import _ from 'lodash';

import infoBoard from '../../core/infoboard';
import worldMap from '../../core/map';
import translate from '../../core/tools/translate';
import environment from '../../environment';
import { CoreControl } from '../CoreControl';
import { VectorType } from '../VectorType';
import { FeaturePermission } from '../../../constants/feature.permission.helper';
import api from '../../api';
import MulticriteriaAfterSalesLayer from '../../layers/AfterSales/MulticriteriaAfterSalesLayer';
import filters from '../../core/filters';

const LayerInfo = (name) => require('../../info').get(name);
const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class MulticriteriaAfterSalesControl extends CoreControl {
    constructor() {
        super({ name: 'MulticriteriaAfterSales' });

        this._toggleLabel = this._toggleLabel.bind(this);
        this.resizeIcon = this.resizeIcon.bind(this);
        this._change = this._change.bind(this);
        this.highlight = this.highlight.bind(this);
        this.unselect = this.unselect.bind(this);
        this.select = this.select.bind(this);
        this.show = this.show.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
    }

    static getInstance() {
        if (!MulticriteriaAfterSalesControl._instance) {
            MulticriteriaAfterSalesControl._instance = new MulticriteriaAfterSalesControl();
        }

        return MulticriteriaAfterSalesControl._instance;
    }

    init() {
        this._tags = {
            tagName: {
                property: 'Name',
                align: 'east',
                size: 10,
                bold: true,
                box: true
            },
            tagScoreTireRetail: {
                property: 'ScoreTireRetail',
                box: true,
                colorize: true,
                color: 'ScoreTireRetailColor',
                type: 'property',
                bold: true,
                size: 12,
                offsetY: -25,
                offsetX: 18,
                mask: "0.0"
            },
            tagScoreTireTruck: {
                property: 'ScoreTireTruck',
                box: true,
                colorize: true,
                color: 'ScoreTireTruckColor',
                type: 'property',
                bold: true,
                size: 12,
                offsetY: -25,
                offsetX: -15,
                mask: "0.0"
            },
            tagScoreAutoParts: {
                property: 'ScoreAutoParts',
                box: true,
                colorize: true,
                color: 'ScoreAutoPartsColor',
                type: 'property',
                bold: true,
                size: 12,
                offsetY: -25,
                offsetX: -49,
                mask: "0.0"
            }
        };

        const values = {
            disableSlider: true,
            help: translate(`Help.${this.options.name}`),
            id: 'layer-multicriteria',
            input: 'checkbox',
            label: translate(`Common.${this.options.name}`),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            type: 'vector-points',
            purge: this.purge,
            tab() {
                return $('#multicriteria-tab-link');
            },
            resize: {
                name: `pinSize${this.options.name}`,
                change: this.resizeIcon,
                type: 'radio'
            },
            dropdown: [],
            checkbox: this._getCheckboxOptions(),
            links: {},
            filter: {
                enable: true,
                click: this.createFilter
            },
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    createFilter() {
        this.filterOptions = {
            ...this.filterOptions,
            ... {
                id: `${_.kebabCase(this.options.name)}-filter-id`,
                label: translate(`Common.${this.options.name}`),
                type: 'multicriteriaAfterSales',
                onSubmit: this._filterCallback
            }
        };

        filters.create(this.filterOptions);
    }

    _filterCallback(values) {
        const selectedFilters = {
            Classification: values.multicriteriaTypes && values.multicriteriaTypes.value ? values.multicriteriaTypes.value : [],
            Color: values.scoreRanges && values.scoreRanges.value ? values.scoreRanges.value : []
        };

        selectedFilters.Classification = _.isArray(selectedFilters.Classification)
            ? selectedFilters.Classification.map(x => x.toString())
            : [selectedFilters.Classification.toString()];

        selectedFilters.Color = _.isArray(selectedFilters.Color)
            ? selectedFilters.Color.map(x => x.toString())
            : [selectedFilters.Color.toString()];

        const filter = this.getLayerFilters();

        _.forEach(selectedFilters, (val, key) => {
            if (_.size(val) || (_.isBoolean(val))) {
                filter.properties[key] = val;
            } else {
                delete filter.properties[key];
            }
        });

        this.setLayerFilters(filter);

        if (!this.check()) {
            this.check(true);
        }
    }

    _change(e) {
        try {
            const layer = this.getLayer();
            this._setFilterButtonVisibility(e.checked);

            if (e.checked) {
                layer.redraw();
            } else {
                layer.hide();
            }
        } catch (err) {
            console.log('ERROR: ', err);
        }
    }

    _setFilterButtonVisibility(state) {
        try {
            Object.keys(this.getControls().button).forEach(item => {
                if (this.getControls().button[item].input) {
                    this.getControls().button[item].input.enable(state);
                }
                this.getControls().button[item].enable = state;
            });
        } catch (err) {
            console.error(err);
        }

        state ? this.createFilter() : filters.close();
    }

    _getCheckboxOptions() {
        const contents = [
            { name: 'tagName', label: translate('Action.ShowTagPointName'), visible: true },
            { name: 'tagScoreAutoParts', label: 'Exibir Score Auto Peças', visible: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_MULTICRITERIA.showScoreAutoparts), type: 'score' },
            { name: 'tagScoreTireTruck', label: 'Exibir Score Pneus Pesados', visible: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_MULTICRITERIA.showScoreTireTruck), type: 'score' },
            { name: 'tagScoreTireRetail', label: 'Exibir Score Pneus Varejo', visible: isAuthorized(FeaturePermission.LAYER_AFTER_SALES_MULTICRITERIA.showScoreTireRetail), type: 'score' },
        ];

        const allows = contents.filter(x => x.visible);
        const onlyOneVisible = allows.length === 2;

        const res = {};
        allows.forEach(content => {
            if (onlyOneVisible && content.type === 'score') {
                this._tags[content.name].offsetY = -25;
                this._tags[content.name].offsetX = 18;
            }

            res[content.label] = {
                name: content.name,
                change: isChecked => {
                    this._toggleLabel(isChecked, content.name);
                },
                style: {
                    change: tag => {
                        this._tags[content.name] = tag;
                        this._toggleLabel();
                    },
                    options: this._tags[content.name]
                }
            };
        });

        return res;
    }

    _getLayerTag() {
        if (!this.getControls().checkbox) { return undefined; }

        const tags = [];

        if ((this.getControls().checkbox.tagName || {}).status) {
            tags.push(_.assign({}, this._tags.tagName));
        }

        if ((this.getControls().checkbox.tagScoreTireRetail || {}).status) {
            tags.push(_.assign({}, this._tags.tagScoreTireRetail));
        }

        if ((this.getControls().checkbox.tagScoreTireTruck || {}).status) {
            tags.push(_.assign({}, this._tags.tagScoreTireTruck));
        }

        if ((this.getControls().checkbox.tagScoreAutoParts || {}).status) {
            tags.push(_.assign({}, this._tags.tagScoreAutoParts));
        }

        return tags.length === 1 ? tags[0] : tags;
    }

    _setLabel() {
        this.getLayer().setOptions({ tag: this._getLayerTag() });
    }

    _toggleLabel(isChecked, name) {
        this._setLabel();
        if (this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    toggleVisibility(id, layer) {
        return this._instance.toggleVisibility(id, layer);
    }

    highlight(marker, event) {
        return this._instance.highlight(marker, event);
    }

    select(marker, type) {
        this._instance.select(marker, type);
    }

    unselect(marker, type) {
        this._instance.unselect(marker, type);
    }

    _view(values) {
        const features = values
            ? _(values).sortBy('properties.Score').reverse().value()
            : null;
        return LayerInfo(this.options.name).create(features);
    }

    show(options = {}, features = this.getLayer().getCurrentFeatures()) {
        if ($('#multicriteria-collapse').length === 0 || options.refresh) {
            if (options.empty) {
                this._view(null);
            } else {
                this._view(features);
            }
        }

        if (options.show) {
            infoBoard.open();
            $('#multicriteria-tab-link').trigger('click');
            if (options.id) {
                const title = $(`#${this.options.name.toLocaleLowerCase()}-title-${options.id}`);
                return setTimeout(() => {
                    if (title[0] && !title[0].isOpen) {
                        title.trigger('select');
                    }

                    if (options.tab === 'summary') {
                        return $(`#button-${this.options.name.toLocaleLowerCase()}-summary-${options.id}`).trigger('click');
                    }
                }, 100);
            }
        }
    }

    setLayerFilters(filter) {
        this.getLayer().setOptions({ filter });

        if (this.check() && this.getLayer().visible()) {
            this.getLayer().reload();
        }
    }

    getLayerFilters() {
        const filter = (this.getLayer().options.filter || {});

        if (!filter.properties) filter.properties = {};

        return filter;
    }

    resizeIcon(value) {
        const { options } = this.getLayer();
        const { symbology } = options;
        const imgSize = {
            sm: '_sm',
            xs: '_xs'
        };

        const sizes = [
            { size: 'large', imgSuffix: '', iconSize: [50, 64], iconAnchor: [25, 60], labelAnchor: [25, -32] },
            { size: 'medium', imgSuffix: imgSize.sm, iconSize: [38, 48], iconAnchor: [19, 48], labelAnchor: [19, -24] },
            { size: 'small', imgSuffix: imgSize.xs, iconSize: [28, 28], iconAnchor: [14, 14], labelAnchor: [14, 0] }
        ];

        const { imgSuffix, iconSize, iconAnchor, labelAnchor, size } = sizes.find(x => x.size === value);
        api.general.log.action(environment._getContextEnum(this.options.name), `Mudou Tam. Icone: ${size}`);

        symbology.style.icon = {
            getUrl: prop => {
                const applyUrl = MulticriteriaAfterSalesLayer.getApplicatorUrlIcon(this.options.name);
                const url = applyUrl(prop);
                return url;
            },
            imgSuffix,
            iconSize,
            iconAnchor,
            labelAnchor
        };

        this.getLayer().setOptions({ symbology });
        if (this.check() && this.getLayer().visible()) {
            return this.getLayer().reload();
        }
    }
}

export default MulticriteriaAfterSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/AfterSales/MulticriteriaAfterSalesControl.js